/**
 * Optional components load only when their markup exists. Keep observing:
 * quick-add, section rendering and the editor can insert them after page load.
 * Core navigation, cart, product forms and variant pickers stay eager, and
 * product pages load their variant:update consumers eagerly (snippets/scripts.liquid).
 */
const components = new Map([
  ['product-sku-component', () => import('@theme/product-sku')],
  ['product-inventory', () => import('@theme/product-inventory')],
  ['volume-pricing', () => import('@theme/volume-pricing')],
  ['price-per-item', () => import('@theme/price-per-item')],
  ['volume-pricing-info', () => import('@theme/volume-pricing-info')],
  ['video-background-component', () => import('@theme/video-background')],
  ['media-gallery', () => import('@theme/media-gallery')],
  ['show-more-component', () => import('@theme/show-more')],
]);

/** @type {Map<string, Promise<unknown>>} */
const loading = new Map();

/**
 * Also accepts detached markup, so quick-add can register its components
 * before inserting content and synchronously dispatching variant events.
 * @param {ParentNode} root
 * @returns {Promise<unknown[]>}
 */
export function loadComponentScripts(root) {
  const pending = [];
  for (const [selector, load] of components) {
    if (customElements.get(selector)) continue;
    if (!(root instanceof Element && root.matches(selector)) && !root.querySelector(selector)) continue;
    let promise = loading.get(selector);
    if (!promise) {
      promise = load().catch((error) => {
        loading.delete(selector);
        throw error;
      });
      loading.set(selector, promise);
    }
    pending.push(promise);
  }
  return Promise.all(pending);
}

/** @param {ParentNode} root */
function loadInsertedComponents(root) {
  loadComponentScripts(root).catch((error) => console.error('Unable to load theme component', error));
}

const observer = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element) loadInsertedComponents(node);
    }
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });
loadInsertedComponents(document);
